import { useEffect, useState } from "react";
import { http } from "../lib/api";
import { formatINR, formatPct } from "../lib/api";
import { motion } from "framer-motion";
import { TrendUp, TrendDown, Wallet, ChartPieSlice, Bank, ArrowRight } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";

const COLORS = ["#10b981", "#f59e0b", "#3b82f6", "#a855f7", "#ef4444", "#06b6d4"];

function Stat({ label, value, sub, tone = "neutral", testId, icon: Icon }) {
  const tones = {
    neutral: "text-zinc-100",
    positive: "text-emerald-400",
    negative: "text-red-400",
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="card-hover bg-zinc-900 border border-zinc-800 rounded-lg p-6"
      data-testid={testId}
    >
      <div className="flex items-start justify-between">
        <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-bold">{label}</div>
        {Icon && <Icon size={18} className="text-zinc-500" />}
      </div>
      <div className={`mt-4 font-display text-3xl ${tones[tone]}`}>{value}</div>
      {sub && <div className={`mt-2 text-sm ${tones[tone]}`}>{sub}</div>}
    </motion.div>
  );
}

export default function Dashboard() {
  const [nw, setNw] = useState(null);
  const [port, setPort] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      const [{ data: n }, { data: p }] = await Promise.all([
        http.get("/summary/networth"),
        http.get("/portfolio/summary"),
      ]);
      setNw(n); setPort(p);
    })();
  }, []);

  const pnlTone = port && port.total_pnl >= 0 ? "positive" : "negative";
  const pnlIcon = port && port.total_pnl >= 0 ? TrendUp : TrendDown;

  const alloc = port
    ? Object.entries(port.allocation).map(([k, v], i) => ({ name: k, value: v, fill: COLORS[i % COLORS.length] }))
    : [];

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-500 font-bold">Overview</div>
          <h1 className="mt-2 font-display text-4xl font-light">Executive Summary</h1>
          <p className="text-zinc-400 mt-2 text-sm">All values in ₹ (Indian Rupees). Live prices via Yahoo Finance.</p>
        </div>
        <div className="hidden md:flex gap-2">
          <Button variant="outline" onClick={() => nav("/portfolio")} className="bg-zinc-900 border-zinc-800" data-testid="quick-add-holding">
            Add holding <ArrowRight size={14} className="ml-2" />
          </Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => nav("/recommendations")} data-testid="quick-open-advisor">
            Get AI advice <ArrowRight size={14} className="ml-2" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Net Worth" value={formatINR(nw?.net_worth ?? 0)} icon={Wallet}
          sub={`${nw?.banks_count ?? 0} bank accounts linked`} testId="stat-net-worth" />
        <Stat label="Portfolio Value" value={formatINR(port?.total_value ?? 0)} icon={ChartPieSlice}
          sub={`${port?.holdings_count ?? 0} holdings`} testId="stat-portfolio-value" />
        <Stat label="Total P&L" value={formatINR(port?.total_pnl ?? 0, { sign: true })} icon={pnlIcon}
          tone={pnlTone} sub={formatPct(port?.total_pnl_pct ?? 0)} testId="stat-total-pnl" />
        <Stat label="Cash Available" value={formatINR(nw?.cash_total ?? 0)} icon={Bank}
          sub="Across all bank accounts" testId="stat-cash-total" />
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Top Holdings</div>
              <div className="font-display text-xl mt-1">Recent performance</div>
            </div>
            <Button size="sm" variant="ghost" className="text-emerald-400 hover:text-emerald-300"
              onClick={() => nav("/portfolio")}>View all →</Button>
          </div>
          {port?.holdings?.length ? (
            <div className="divide-y divide-zinc-800">
              {port.holdings.slice(0, 6).map((h) => (
                <div key={h.id} className="py-3 flex items-center justify-between" data-testid={`dashboard-holding-${h.symbol}`}>
                  <div>
                    <div className="font-medium">{h.symbol} <span className="text-xs text-zinc-500 ml-2">{h.exchange}</span></div>
                    <div className="text-xs text-zinc-500">{h.quantity} × {formatINR(h.avg_price)}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono">{formatINR(h.market_value)}</div>
                    <div className={`text-xs ${h.pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {formatINR(h.pnl, { sign: true })} · {formatPct(h.pnl_pct)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-zinc-500 py-8 text-center">No holdings yet. Add your first from Portfolio →</div>
          )}
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6">
          <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Allocation</div>
          <div className="font-display text-xl mt-1 mb-4">By asset type</div>
          {alloc.length ? (
            <>
              <div className="h-56">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={alloc} dataKey="value" innerRadius={55} outerRadius={85} stroke="#09090b" strokeWidth={2}>
                      {alloc.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 8, color: "#f4f4f5" }}
                      formatter={(v) => formatINR(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 space-y-2 text-sm">
                {alloc.map((a, i) => (
                  <div key={a.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                      <span className="text-zinc-300">{a.name}</span>
                    </div>
                    <span className="font-mono text-zinc-400">{formatINR(a.value, { decimals: 0 })}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-sm text-zinc-500 py-8 text-center">Nothing to chart yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
